import React, { createContext, useEffect, useState } from "react";
import { IResolutionsContextType } from "../types/interfaces/IResolutionsContextType";
import { IResolutionEvent } from "../types/interfaces/IResolutionEvent";
import { IResolutionsProviderProps } from "../types/interfaces/IResolutionsProviderProps";
import { IResolutionsStaticText } from "../types/interfaces/IResolutionsStaticText";
import { useErrorContext } from "../../global/hooks/useErrorContext";
import { useAuthenticationContext } from "../../global/hooks/useAuthenticationContext";

//The resolution context object holds the latest value of the state.
export const ResolutionsContext = createContext<IResolutionsContextType | undefined>(undefined);

//This component is responsible to share the resolution events data between components.
export const ResolutionsProvider: React.FC<IResolutionsProviderProps> = ({ children }) => {
    const [resolutionEvents, setResolutionEvents] = useState<IResolutionEvent[] | null>(null);
    const [selectedEvent, setSelectedEvent] = useState<IResolutionEvent | null>(null);
    const [staticText, setStaticText] = useState<IResolutionsStaticText | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const { setError } = useErrorContext();
    const { cstKey } = useAuthenticationContext();

    const API_RESOLUTIONS = process.env.REACT_APP_API_RESOLUTIONS;
    const INELIGIBLE_ERROR_TEXT: string | null = process.env.REACT_APP_INELIGIBLE_ERROR_TEXT ?? null;

    const handleError = (message: string | null, statusCode: number) => {  
        setError({ message, statusCode });
        setLoading(false);
    };

    //Function to fetch data using the get resolution events api endpoint.
    const ResolutionEventsFetch = async () => {
        try {
            const apiGetResolutionEvents = `${API_RESOLUTIONS}${cstKey}`;
            const response = await fetch(apiGetResolutionEvents);
            if (!response.ok) {
                console.log('get', response.statusText);
                if (response.status === 401 || response.status === 403) {
                    return handleError(INELIGIBLE_ERROR_TEXT, response.status);
                } else {
                    return handleError(null, 1);
                }
            }
            const result = await response.json();
            if (!result) {
                return handleError(null, 2);
            } else {
                if (result.CustomerKey === null || result.Data === null || (!result.Succeeded && result.Errors !== null)) {
                    return handleError(null, 3);
                }
                if (result.Data && result.Data[0].Resolutions === null && result.Data[0].ResolutionsStaticText !== null) {
                    return handleError(result.Data[0].ResolutionsStaticText.NoResolutionsText, 4);
                }
                setResolutionEvents(result.Data);
                setSelectedEvent(result.Data[0]);
                setStaticText(result.Data[0].ResolutionsStaticText);
            }
        } catch (error: any) {
            console.log('get error', error);
            handleError(null, error.status);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (cstKey !== null && cstKey !== undefined) {
            ResolutionEventsFetch();
        }
    }, [cstKey]);

    const value = {
        resolutionEvents,
        selectedEvent,
        staticText,
        loading,
        cstKey,
        setResolutionEvents,
        setSelectedEvent
    }

    return (
        <ResolutionsContext.Provider value={value}>
            {children}
        </ResolutionsContext.Provider>
    );
}